import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { loadAudioPlaylist, flattenTracks, audioTrackUrl } from '../../services/audioLectures';
import { LearningProgressService } from '../../services/LearningProgressService';

// Recorded-phrase audio lectures — a simple continuous-playback playlist,
// grouped into themed collections (Greetings, Questions, ...), for whichever
// target language has data-audio/{lang}/playlist.json (only a subset of
// target languages do; degrades to a friendly "not available yet" state for
// the rest). Playback (auto-advance, next/prev) runs across the whole
// flattened playlist, not just within one collection tab. Mirrors the
// mobile app's AudioLecturesScreen.
export default function AudioLecturesPage() {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const targetCode = languagePrefs?.targetLanguage || 'en';
  const nativeCode = languagePrefs?.nativeLanguage || 'en';

  const [loading, setLoading] = useState(true);
  const [playlist, setPlaylist] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);

  const audioRef = useRef(null);
  const flatTracks = useMemo(() => flattenTracks(playlist), [playlist]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPlaylist(null);
    setCurrentIndex(null);
    setActiveTab(0);
    audioRef.current?.pause();
    audioRef.current = null;
    setIsPlaying(false);
    loadAudioPlaylist(targetCode).then((data) => {
      if (!active) return;
      setPlaylist(data);
      setLoading(false);
    });
    return () => { active = false; };
  }, [targetCode]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${t('audioLecturesPage.title')} | SublingoX`;
    return () => { document.title = previousTitle; };
  }, [t]);

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  function playIndex(index) {
    if (index < 0 || index >= flatTracks.length) return;
    if (currentIndex === index) {
      if (isPlaying) { audioRef.current?.pause(); } else { audioRef.current?.play().catch(() => {}); }
      return;
    }

    setCurrentIndex(index);
    setError(null);
    const track = flatTracks[index];
    audioRef.current?.pause();
    const audio = new Audio(audioTrackUrl(targetCode, track));
    audioRef.current = audio;
    audio.onplay = () => setIsPlaying(true);
    audio.onpause = () => setIsPlaying(false);
    audio.onended = () => playNext(index);
    audio.onerror = () => {
      setCurrentIndex(null);
      setIsPlaying(false);
      setError(t('audioLecturesPage.playError'));
    };
    audio.play().catch(() => {
      setCurrentIndex(null);
      setIsPlaying(false);
      setError(t('audioLecturesPage.playError'));
    });

    LearningProgressService.record(`${nativeCode}_${targetCode}`, 'listen', 1, {
      activityId: `audiolecture:${targetCode}:${track.id}`,
    });
  }

  function playNext(fromIndex) {
    const next = (fromIndex ?? currentIndex ?? -1) + 1;
    if (next < flatTracks.length) {
      playIndex(next);
    } else {
      setCurrentIndex(null);
      audioRef.current?.pause();
    }
  }

  function playPrevious() {
    if (currentIndex == null || currentIndex <= 0) return;
    playIndex(currentIndex - 1);
  }

  const collections = playlist?.collections || [];
  const offsets = [];
  let running = 0;
  for (const collection of collections) {
    offsets.push(running);
    running += collection.tracks.length;
  }
  const currentTrack = currentIndex != null ? flatTracks[currentIndex] : null;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <h1 className="text-xl font-black text-slate-900">{t('audioLecturesPage.title')}</h1>
        {!loading && collections.length > 0 && (
          <p className="mt-0.5 text-xs text-slate-500">{t('audioLecturesPage.desc')}</p>
        )}
      </header>

      <div className="mx-auto w-full max-w-2xl flex-1 px-4 py-4 sm:px-6">
        {loading && (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((item) => <div key={item} className="h-16 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        )}

        {!loading && collections.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">🎧</span>
            <p className="mt-3 text-sm text-slate-500">{t('audioLecturesPage.unavailable')}</p>
          </div>
        )}

        {!loading && collections.length > 0 && (
          <>
            {error && (
              <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800">
                {error}
              </div>
            )}
            <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
              <div className="flex gap-1.5 border-b border-slate-200 pb-px">
                {collections.map((collection, index) => (
                  <button
                    key={collection.id || index}
                    type="button"
                    onClick={() => setActiveTab(index)}
                    className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-bold transition ${
                      activeTab === index ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {collection.icon} {collection.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 space-y-2 pb-28">
              {collections[activeTab]?.tracks.map((track, i) => {
                const index = offsets[activeTab] + i;
                return (
                  <TrackRow
                    key={track.id}
                    track={track}
                    current={currentIndex === index}
                    playing={currentIndex === index && isPlaying}
                    onTap={() => playIndex(index)}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      {currentTrack && (
        <div className="fixed inset-x-0 bottom-0 bg-gradient-to-r from-indigo-700 to-violet-700 px-4 py-2.5 shadow-lg sm:px-6">
          <div className="mx-auto flex max-w-2xl items-center gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-wide text-white/70">{t('audioLecturesPage.nowPlaying')}</p>
              <p className="truncate text-sm font-bold text-white">{currentTrack.title}</p>
            </div>
            <button
              type="button"
              onClick={playPrevious}
              disabled={currentIndex === 0}
              aria-label={t('audioLecturesPage.previous', 'Previous')}
              className="p-2 text-white disabled:opacity-30"
            >
              ⏮
            </button>
            <button
              type="button"
              onClick={() => playIndex(currentIndex)}
              aria-label={isPlaying ? t('audioLecturesPage.pause', 'Pause') : t('audioLecturesPage.play', 'Play')}
              className="p-1 text-3xl text-white"
            >
              {isPlaying ? '⏸' : '▶️'}
            </button>
            <button
              type="button"
              onClick={() => playNext()}
              disabled={currentIndex == null || currentIndex >= flatTracks.length - 1}
              aria-label={t('audioLecturesPage.next', 'Next')}
              className="p-2 text-white disabled:opacity-30"
            >
              ⏭
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TrackRow({ track, current, playing, onTap }) {
  return (
    <button
      type="button"
      onClick={onTap}
      className={`flex w-full flex-col rounded-2xl border px-3.5 py-3 text-left transition ${
        current ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl text-indigo-600">{playing ? '⏸' : '▶️'}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-slate-900">{track.title}</p>
          {track.translation && <p className="truncate text-xs text-slate-400">{track.translation}</p>}
        </div>
        {track.lyrics && <span className="shrink-0 text-slate-400">{current ? '▲' : '🎵'}</span>}
      </div>
      {current && track.lyrics && (
        <div className="mt-3 space-y-3 pl-9 pr-2">
          {track.lyrics.map((stanza, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={i} className="whitespace-pre-line text-sm leading-relaxed text-slate-600">
              {stanza.join('\n')}
            </p>
          ))}
        </div>
      )}
    </button>
  );
}
